import React, { useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "./LocationButton.css";
import { useTranslation } from "react-i18next";

const LocationButton = ({ onError, onLocationFound }) => {
  const map = useMap();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation();

  // Hedef/crosshair ikonu ile yanıp sönen marker oluştur
  const createLocationMarker = (lat, lng) => {
    const customIcon = new L.DivIcon({
      className: "custom-user-marker",
      html: `
        <div style="
          width: 24px;
          height: 24px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: locationPulse 2s ease-in-out infinite, fadeOut 1s ease-out 3s forwards;
        ">
          <!-- Hedef/crosshair ikonu -->
          <svg 
            viewBox="0 0 40 40" 
            width="24" 
            height="24" 
            style="
              position: absolute;
              filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
            "
          >
            <!-- Dış halka -->
            <circle 
              cx="20" 
              cy="20" 
              r="14" 
              fill="none" 
              stroke="#0f66f3" 
              stroke-width="1.5"
              opacity="0.8"
            />
            <!-- Ortadaki mavi daire -->
            <circle 
              cx="20" 
              cy="20" 
              r="6" 
              fill="#0f66f3"
            />
          </svg>
          
          <!-- Yanıp sönen halka efekti -->
          <div style="
            position: absolute;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: 1.5px solid rgba(15, 102, 243, 0.4);
            animation: locationRipple 2s ease-out infinite;
          "></div>
        </div>
        <style>
          @keyframes locationPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.1);
              opacity: 0.85;
            }
          }
          
          @keyframes locationRipple {
            0% {
              transform: scale(0.9);
              opacity: 0.8;
            }
            100% {
              transform: scale(1.8);
              opacity: 0;
            }
          }
          
          @keyframes fadeOut {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }
        </style>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    // Marker oluştur
    const marker = L.marker([lat, lng], { icon: customIcon }).addTo(map);

    // 4 saniye sonra marker'ı kaldır (3s görünür + 1s fade-out)
    setTimeout(() => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    }, 4000);
  };

  const handleLocationClick = () => {
    if (!navigator.geolocation) {
      const errorMessage = t("location.notSupported");
      if (onError) {
        onError(errorMessage);
      } else {
        alert(errorMessage);
      }
      return;
    }

    setIsLoading(true);
    setHasError(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        // Haritayı konuma götür (smooth animation ile)
        map.flyTo([latitude, longitude], 16, {
          animate: true,
          duration: 1,
        });

        // Mavi dalga efekti ile marker ekle
        createLocationMarker(latitude, longitude);

        // Parent'a konum bilgisini ilet (mavi nokta marker için)
        if (onLocationFound) {
          onLocationFound({ lat: latitude, lng: longitude });
        }

        setIsLoading(false);
      },
      (error) => {
        console.error("Konum alınamadı:", error);
        setHasError(true);
        setIsLoading(false);

        // iOS tespiti
        const isIOS = /iphone|ipad|ipod/.test(
          window.navigator.userAgent.toLowerCase()
        );
        const isSafari =
          /safari/.test(window.navigator.userAgent.toLowerCase()) &&
          !/chrome|chromium|fxios|crios/.test(
            window.navigator.userAgent.toLowerCase()
          );
        const isIOSSafari = isIOS && isSafari;

        // Hata mesajını belirle
        let errorMessage = t("location.failed");
        switch (error.code) {
          case error.PERMISSION_DENIED:
            if (isIOSSafari) {
              errorMessage = t("location.deniedIOS");
            } else {
              errorMessage = t("location.denied");
            }
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = t("location.unavailable");
            break;
          case error.TIMEOUT:
            errorMessage = t("location.timeout");
            break;
          default:
            errorMessage = t("location.generic");
            break;
        }

        // Toast notification göster
        if (onError) {
          onError(errorMessage);
        }

        // 2 saniye sonra hata durumunu temizle
        setTimeout(() => setHasError(false), 2000);
      },
      {
        enableHighAccuracy: false, // Daha hızlı, network-based konum
        timeout: 5000, // 5 saniye timeout (daha kısa)
        maximumAge: 30000, // 30 saniye cache (son konumu kullan)
      }
    );
  };

  return (
    <button
      className={`location-button ${isLoading ? "loading" : ""} ${
        hasError ? "error" : ""
      }`}
      onClick={handleLocationClick}
      disabled={isLoading}
      title={t("map.goToLocation")}
    >
      {isLoading ? (
        <div className="location-spinner"></div>
      ) : hasError ? (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          className="location-icon"
        >
          <path
            fill="currentColor"
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
          />
        </svg>
      ) : (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          className="location-icon"
        >
          <path
            fill="currentColor"
            d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"
          />
        </svg>
      )}
    </button>
  );
};

export default LocationButton;
